import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartFrame, { TOOLTIP_STYLE } from './ChartFrame';
import { fmtMoney, fmtNumber } from '../derive';

const STATUS_COLORS = {
  completed: '#10b981',
  paymentCompleted: '#0ea5e9',
  picked: '#6366f1',
  pending: '#f59e0b',
  requested: '#f43f5e',
};

// Orders activity: a daily volume bar chart plus a compact status mix
// strip. Both derive from the existing filteredOrders list — no new fetch.
export default function CanvasOrders({
  series = [],
  total = 0,
  revenue = 0,
  aov = 0,
  statusMix = [],
}) {
  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr_auto] gap-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-zinc-200 bg-white px-2 py-1.5">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500">Orders</p>
          <p className="mt-0.5 text-[15px] font-semibold tabular-nums text-zinc-900">{fmtNumber(total)}</p>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white px-2 py-1.5">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500">Volume</p>
          <p className="mt-0.5 text-[15px] font-semibold tabular-nums text-zinc-900">{fmtMoney(revenue)}</p>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white px-2 py-1.5">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500">AOV</p>
          <p className="mt-0.5 text-[15px] font-semibold tabular-nums text-zinc-900">{fmtMoney(aov)}</p>
        </div>
      </div>

      <ChartFrame title="Orders per day">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
            <CartesianGrid stroke="#f4f4f5" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveEnd" />
            <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
            <Bar dataKey="count" fill="#6366f1" radius={[3, 3, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartFrame>

      {statusMix.length > 0 ? (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Status mix</p>
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-zinc-100">
            {statusMix.map((s) => {
              const pct = total ? (s.count / total) * 100 : 0;
              return (
                <div
                  key={s.status}
                  title={`${s.status}: ${s.count} (${Math.round(pct)}%)`}
                  className="h-full transition-[width] duration-300 motion-reduce:transition-none"
                  style={{ width: `${pct}%`, background: STATUS_COLORS[s.status] || '#a1a1aa' }}
                />
              );
            })}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {statusMix.map((s) => (
              <span key={s.status} className="inline-flex items-center gap-1 text-[10px] text-zinc-600">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: STATUS_COLORS[s.status] || '#a1a1aa' }}
                />
                <span className="font-medium text-zinc-800">{s.status}</span>
                <span className="tabular-nums text-zinc-500">{fmtNumber(s.count)}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Re-export status colors so RecordsSheet / leaderboards can match.
export { STATUS_COLORS };
